import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchLayout, type LayoutDetail } from '../lib/api';
import { GRID_RENDER_KINDS, RENDER_LABELS } from '../lib/deck';
import { formatCode, formatCents, formatDimensions } from '../lib/format';
import { PlaceholderRender } from '../components/PlaceholderRender';

export function LayoutDetailPage() {
  const { code } = useParams<{ code: string }>();
  const [layout, setLayout] = useState<LayoutDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setLayout(null);
    setError(null);
    fetchLayout(code)
      .then((r) => !cancelled && setLayout(r.layout))
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (error) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <Link to="/" className="text-sm text-vari-blue-accessible hover:underline">
            ← Back to catalog
          </Link>
          <div className="mt-6 rounded-md border border-vari-red bg-vari-red/10 text-vari-red px-4 py-3 text-sm">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-10 text-vari-gray text-sm">Loading…</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-vari-beige/20">
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <div>
          <Link
            to={`/?family=${layout.family.slug}`}
            className="text-sm text-vari-blue-accessible hover:underline"
          >
            ← {layout.family.name}
          </Link>
          <div className="mt-2 flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl font-semibold text-vari-navy">#{formatCode(layout.code)}</h1>
              <p className="text-vari-gray text-sm mt-1">
                {layout.family.name} · {formatDimensions(layout.widthInches, layout.depthInches)} ·{' '}
                {layout.items.length} items
              </p>
            </div>
            <div className="text-2xl font-semibold text-vari-navy">
              {formatCents(layout.totalPriceCents)}
            </div>
          </div>
        </div>

        {/* Hero render */}
        <section className="rounded-2xl overflow-hidden border border-vari-soft-gray bg-white shadow-sm">
          <PlaceholderRender
            variant="hero"
            label={`Master · #${formatCode(layout.code)}`}
            src={layout.renders.master?.fullUrl ?? null}
          />
        </section>

        {/* Renders */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold text-vari-blue-accessible uppercase tracking-[0.2em]">
            Renders
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {GRID_RENDER_KINDS.map((kind) => (
              <div
                key={kind}
                className="rounded-lg overflow-hidden border border-vari-soft-gray bg-white"
              >
                <PlaceholderRender
                  variant="thumb"
                  label={RENDER_LABELS[kind]}
                  src={layout.renders[kind]?.thumbUrl ?? null}
                  fit={kind === 'dimensional' ? 'contain' : 'cover'}
                />
              </div>
            ))}
          </div>
        </section>

        {/* BOM */}
        <section className="space-y-3">
          <h2 className="text-xs font-semibold text-vari-blue-accessible uppercase tracking-[0.2em]">
            Bill of materials
          </h2>
          <div className="rounded-lg border border-vari-soft-gray bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-vari-beige/40 text-vari-gray text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Description</th>
                  <th className="text-left px-4 py-2 font-medium">SKU</th>
                  <th className="text-right px-4 py-2 font-medium">Qty</th>
                  <th className="text-right px-4 py-2 font-medium">Unit</th>
                  <th className="text-right px-4 py-2 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {layout.items.map((item) => (
                  <tr key={item.sku} className="border-t border-vari-soft-gray/40">
                    <td className="px-4 py-2 text-vari-navy">{item.description}</td>
                    <td className="px-4 py-2 text-vari-gray font-mono text-xs">{item.sku}</td>
                    <td className="px-4 py-2 text-right text-vari-navy">{item.quantity}</td>
                    <td className="px-4 py-2 text-right text-vari-navy">
                      {formatCents(item.unitPriceCents)}
                    </td>
                    <td className="px-4 py-2 text-right text-vari-navy font-medium">
                      {formatCents(item.lineTotalCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-vari-navy/20 bg-vari-beige/40">
                  <td colSpan={4} className="px-4 py-2 text-right font-medium text-vari-gray">
                    Total
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-vari-navy">
                    {formatCents(layout.totalPriceCents)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
